import { useEffect, useId, useRef, useState } from 'react';
import mermaid from 'mermaid';
import { useTheme } from '../../context/ThemeContext';

type MermaidDiagramProps = {
  chart: string;
  className?: string;
  caption?: string;
};

export default function MermaidDiagram({ chart, className = '', caption }: MermaidDiagramProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const { theme } = useTheme();
  const reactId = useId().replace(/:/g, '');

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    let cancelled = false;

    const render = async () => {
      try {
        setError(null);
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: 'strict',
          theme: theme === 'dark' ? 'dark' : 'default',
          flowchart: {
            useMaxWidth: true,
            htmlLabels: true,
            curve: 'basis',
            padding: 16,
            nodeSpacing: 40,
            rankSpacing: 48,
          },
        });

        const renderId = `mmd-${reactId}-${Date.now()}`;
        const { svg } = await mermaid.render(renderId, chart.trim());
        if (!cancelled) {
          el.innerHTML = svg;
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Diagram failed to render');
          el.innerHTML = '';
        }
      }
    };

    void render();

    return () => {
      cancelled = true;
    };
  }, [chart, theme, reactId]);

  return (
    <figure className={className}>
      <div
        className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/80 p-4 sm:p-6 overflow-x-auto flex justify-center min-h-[12rem] [&_svg]:max-w-full [&_svg]:h-auto"
        aria-label="Pipeline flow diagram"
      >
        {error ? (
          <div className="text-sm text-red-600 dark:text-red-400 self-center text-center px-4">
            <p className="font-medium">Could not render diagram</p>
            <p className="text-xs mt-1 font-mono">{error}</p>
          </div>
        ) : (
          <div ref={containerRef} className="w-full flex justify-center" />
        )}
      </div>
      {caption ? (
        <figcaption className="mt-2 text-xs text-center text-gray-500 dark:text-gray-400">
          {caption}
        </figcaption>
      ) : null}
    </figure>
  );
}
